
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState, mapGetters } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import BrandsStore from "@/mixins/brandsStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { DefaultFilters } from "@/data/filters/exchangeRates";
import { DefaultSorters } from "@/data/sorters/exchangeRates";

// Types
import {
  IExchangeRatesProvider,
  IExchangeRatesProviderMethods
} from "@/models/providers/exchangeRates";
import { ISorter, IFilter } from "@/models/tables";
import { IExchangeRate, IExchangeRateForm } from "@/models/exchangeRates";
import { IState } from "@/store";
import { ICurrency } from "@/models/constants";

@Component({
  computed: {
    ...mapState({
      currenciesScope(state: IState, getters) {
        return getters[`data/${DataModules.CURRENCIES}/scope`](
          this.isOrgMode ? "$organisation" : this.brandId
        );
      },
      availableCurrencies(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.CURRENCIES,
          scope: this.currenciesScope
        });
      }
    }),
    ...mapGetters({
      isOrgMode: "brand/isMultibrand"
    })
  }
})
export default class ExchangeRatesProvider extends Mixins(
  FilteringSortingTableStore,
  BrandsStore,
  CurrenciesStore
) {
  // Map state
  public availableCurrencies!: ICurrency[];
  public currenciesScope!: string;

  // Getters

  public isOrgMode!: boolean;

  // Props

  @Prop({ default: "ERLP1" /** [E]xchange [R]ates [L]ist [P]rovider 1 */ })
  public declare id: string;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultFilters() })
  public availableFilters!: IFilter[];

  @Prop({ default: () => DefaultSorters() })
  public availableSorters!: ISorter[];

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  // Data

  public dataModule = DataModules.MANUAL_EXCHANGE_RATES;
  public filters = this.availableFilters;
  public sorters = this.availableSorters;
  public isLoading = true;
  public isReloading = false;

  // Life-cycle Methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CURRENCIES,
      scope: this.currenciesScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await this.getCurrencies();
      await this.getData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public getCurrencies() {
    return this.$store.dispatch(`data/${DataModules.CURRENCIES}/list`, {
      type: this.isOrgMode ? "organisation" : "brand",
      scope: this.currenciesScope,
      params: {
        brand_id: this.isOrgMode ? undefined : this.brandId,
        limit: 0
      },
      ignoreStored: true,
      vm: this
    });
  }

  public async deleteExchangeRate(exchangeRateId: IExchangeRate["id"]) {
    try {
      this.isLoading = true;
      await this.deleteRate(exchangeRateId);
      await this.reloadData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
    }
  }

  public deleteRate(exchangeRateId: IExchangeRate["id"]) {
    if (!this.canDelete) return Promise.resolve();

    return this.$store.dispatch(
      `data/${DataModules.MANUAL_EXCHANGE_RATES}/delete`,
      { exchangeRateId }
    );
  }

  public updateRate(exchangeRateId: IExchangeRate["id"]) {
    if (!this.canUpdate) return Promise.resolve();

    return this.$store.dispatch(
      `data/${DataModules.MANUAL_EXCHANGE_RATES}/delete`,
      {
        exchangeRateId,
        data: { active: false }
      }
    );
  }

  public createRate(exchangeRate: IExchangeRateForm) {
    if (!this.canCreate) return Promise.resolve();

    return this.$store.dispatch(
      `data/${DataModules.MANUAL_EXCHANGE_RATES}/create`,
      { data: exchangeRate }
    );
  }

  public async reloadData() {
    try {
      this.isReloading = true;
      await this.getData({ ignoreStored: true });
    } finally {
      this.isReloading = false;
    }
  }

  public getStoreParams(params: any): Promise<any> {
    return _.merge(
      {
        params: {
          ...this.manualFilters
        }
      },
      params // Important – must merge last
    );
  }

  // Computed

  public get canCreate() {
    return this.$userCan("store_manual_exchange_rate");
  }

  public get canDelete() {
    return this.$userCan("destroy_manual_exchange_rate");
  }

  public get canUpdate() {
    return this.$userCan("update_manual_exchange_rate");
  }

  // Guarding

  public guardFunctionalities = ["list_manual_exchange_rates"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("exchangeRatesProvider")
  public providerData(): IExchangeRatesProvider {
    return {
      availableCurrencies: _.orderBy(this.availableCurrencies, ["code", "asc"]),
      availableFilters: this.filters,
      availableSorters: this.sorters,
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      canCreate: this.canCreate,
      canDelete: this.canDelete,
      canUpdate: this.canUpdate,
      currencies: this.currencies,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      exchangeRates: this.table.data as IExchangeRate[],
      guardFunctionalities: this.guardFunctionalities,
      isLoading: this.isLoading || this.table.loading,
      isOrgMode: this.isOrgMode,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      requestError: this.requestError,
      table: this.table,
      total: this.table.total
    };
  }

  @Provide("exchangeRatesProviderMethods")
  public get providerMethods(): IExchangeRatesProviderMethods {
    return {
      createRate: this.createRate,
      deleteExchangeRate: this.deleteExchangeRate,
      deleteRate: this.deleteRate,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData,
      updateRate: this.updateRate
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $exchangeRatesData: this.providerData(),
        $exchangeRatesMethods: this.providerMethods
      })
    );
  }
}
